import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Banknote, TrendingUp, Users, AlertTriangle, ArrowUpRight, Activity, Wallet, Gavel } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar } from 'recharts';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { KpiCard } from '@/components/shared/KpiCard';
import { StatusChip } from '@/components/shared/StatusChip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MANAGER_KPIS, RECOVERY_TREND, CHANNEL_PERFORMANCE, PORTFOLIO_MIX, PORTFOLIOS } from '@/data/seed';
import { formatSAR, formatPct, formatNumber } from '@/lib/format';
import { useApp } from '@/context/AppContext';
import { usePageTitle } from '@/lib/usePageTitle';

const KPI_ICON = { assigned: Banknote, recovered: Wallet, recRate: TrendingUp, active: Users, ptpToday: Activity, channelConv: ArrowUpRight, compliance: AlertTriangle, legal: Gavel };

const KPI_JARGON = {
  recRate: 'Collection Effectiveness Index (CEI) proxy — recovered ÷ collectable, expressed as a percentage. SAMA-aligned definition.',
  channelConv: 'Outbound WhatsApp messages that produced a debtor reply within 24h, as % of total sent.',
  ptpToday: 'Promises To Pay (PTP) scheduled to fall due before end-of-day, across all agents.',
  compliance: 'Open compliance flags requiring supervisor review (SAMA window breaches, opt-out hits, dispute holds).',
  legal: 'Cases that have crossed the pre-legal threshold and are queued or active in court process.',
};

export default function CommandCenter() {
  usePageTitle('Command Center');
  const navigate = useNavigate();
  const { events } = useApp();

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader
        eyebrow={`Manager view • ‎ ${new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' })}`}
        title="Recovery Command Center"
        subtitle="Real-time view of portfolios, recovery outcomes, channel performance and compliance posture across Alwasl operations."
        actions={(
          <>
            <Button size="sm" className="bg-secondary text-secondary-foreground hover:bg-secondary/90" onClick={() => navigate('/app/portfolios')}>Import portfolio</Button>
          </>
        )}
      />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {MANAGER_KPIS.map((k) => (
          <KpiCard key={k.id} {...k} icon={KPI_ICON[k.id]} accent={k.id === 'recovered'} jargon={KPI_JARGON[k.id]} />
        ))}
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="surface-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Recovery vs. Assigned</p>
              <h3 className="text-base font-semibold mt-0.5">Last 6 months — SAR (millions)</h3>
            </div>
            <Badge variant="outline" className="text-success border-success/40 bg-success/10">+12.6% MoM</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={RECOVERY_TREND}>
                <defs>
                  <linearGradient id="gAssigned" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gRecovered" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="m" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="assigned" stroke="hsl(var(--secondary))" fill="url(#gAssigned)" strokeWidth={2} />
                <Area type="monotone" dataKey="recovered" stroke="hsl(var(--primary))" fill="url(#gRecovered)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface-card p-5">
          <p className="text-[10px] uppercase tracking-widest text-primary">Portfolio mix</p>
          <h3 className="text-base font-semibold mt-0.5 mb-2">By client sector</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={PORTFOLIO_MIX} dataKey="value" innerRadius={48} outerRadius={78} paddingAngle={3} strokeWidth={0}>
                  {PORTFOLIO_MIX.map((_, i) => (<Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />))}
                </Pie>
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            {PORTFOLIO_MIX.map((p, i) => (
              <div key={p.name} className="flex items-center gap-1.5 text-[11px]">
                <span className="h-2 w-2 rounded-sm" style={{ background: `hsl(var(--chart-${(i % 5) + 1}))` }} />
                <span className="text-muted-foreground">{p.name}</span>
                <span className="ml-auto font-mono-num">{p.value}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Channel + Portfolio table */}
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="surface-card p-5 lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Channel performance</p>
              <h3 className="text-base font-semibold mt-0.5">Outreach funnel — last 30 days</h3>
            </div>
            <Badge variant="outline" className="text-[10px]">All channels mocked</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={CHANNEL_PERFORMANCE} barCategoryGap={22}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="channel" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="delivered" stackId="a" fill="hsl(var(--muted))" radius={[0, 0, 4, 4]} />
                <Bar dataKey="read" stackId="a" fill="hsl(var(--secondary))" />
                <Bar dataKey="replied" stackId="a" fill="hsl(var(--info))" />
                <Bar dataKey="converted" stackId="a" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="surface-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-primary">Live event stream</p>
              <h3 className="text-base font-semibold mt-0.5">Integration & audit log</h3>
            </div>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/app/audit')}>Open audit →</Button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin pr-1">
            {events.slice(0, 10).map((e) => (
              <motion.div key={e.eventId} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }} className="flex items-start gap-2 text-[12px]">
                <span className={`mt-1 h-1.5 w-1.5 rounded-full shrink-0 ${e.status === 'success' ? 'bg-success' : e.status === 'failed' ? 'bg-destructive' : 'bg-warning'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium truncate">{e.type}</span>
                    <span className="text-muted-foreground text-[10px]">{e.latencyMs}ms</span>
                  </div>
                  <p className="text-muted-foreground truncate text-[11px]">{e.src} → {e.dst} • {e.payload}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Portfolio table */}
      <div className="surface-card overflow-hidden">
        <div className="p-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-primary">Client portfolios</p>
            <h3 className="text-base font-semibold mt-0.5">Health, ageing & recovery readiness</h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/app/portfolios')}>Manage portfolios</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Portfolio</th>
                <th className="text-left px-3 py-2.5 font-medium">Client</th>
                <th className="text-right px-3 py-2.5 font-medium">Accounts</th>
                <th className="text-right px-3 py-2.5 font-medium">Value</th>
                <th className="text-left px-3 py-2.5 font-medium">Ageing mix</th>
                <th className="text-left px-3 py-2.5 font-medium">Health</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {PORTFOLIOS.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-muted/40 transition-colors">
                  <td className="px-5 py-3 font-medium">{p.id}</td>
                  <td className="px-3 py-3 text-muted-foreground">{p.clientName}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{formatNumber(p.accounts)}</td>
                  <td className="px-3 py-3 text-right font-mono-num">{formatSAR(p.value, { compact: true })}</td>
                  <td className="px-3 py-3">
                    <AgeingBar buckets={p.ageingBuckets} />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${p.healthScore}%` }} />
                      </div>
                      <span className="text-xs font-mono-num">{p.healthScore}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3"><StatusChip status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const AgeingBar = ({ buckets }) => {
  const colors = ['bg-success', 'bg-primary/70', 'bg-primary', 'bg-warning', 'bg-destructive'];
  return (
    <div className="flex h-1.5 w-40 rounded-full overflow-hidden border border-border">
      {Object.values(buckets).map((v, i) => (
        <div key={i} className={colors[i]} style={{ width: `${v}%` }} title={`${Object.keys(buckets)[i]}: ${v}%`} />
      ))}
    </div>
  );
};
